import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import {
  Plus, MoreVertical, Pencil, Trash2, Share2, PlusCircle, GraduationCap, Layers,
  Folder, FolderOpen, FolderPlus, ArrowLeft, FolderInput, Users, Lock, GripVertical,
} from 'lucide-react';

const LEVELS = { beginner: { label: 'Beginner', cls: 'text-emerald-600' }, intermediate: { label: 'Intermediate', cls: 'text-amber-600' }, advanced: { label: 'Advanced', cls: 'text-rose-600' } };
const fmtTime = (secs) => { const s = Math.max(0, secs || 0); const m = Math.floor(s/60); const r = s%60; return `${String(m).padStart(2,'0')}:${String(r).padStart(2,'0')} min`; };
const fmtDate = (iso) => { try { return new Date(iso).toLocaleString('es', { hour:'2-digit', minute:'2-digit', day:'numeric', month:'short', year:'numeric' }); } catch { return ''; } };
const NO_FOLDER = '__none__';
const emptyForm = { name:'', description:'', module:'', level:'beginner', visibility:'private', folder_id: NO_FOLDER };

export default function StudiesPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const isOwnerAdmin = ['super_admin', 'school_admin'].includes(user?.role);
  const base = isOwnerAdmin ? '/admin/studies' : '/teacher/studies';

  const [tab, setTab] = useState('study');
  const [sub, setSub] = useState(isOwnerAdmin ? 'mine' : 'shared');
  const [studies, setStudies] = useState({ mine: [], shared: [], public: [], assigned: [] });
  const [folders, setFolders] = useState([]);
  const [unfolderedCount, setUnfolderedCount] = useState(0);
  const [currentFolderId, setCurrentFolderId] = useState(null); // null = folder list, 'id' = folder, NO_FOLDER = unfoldered
  const [q, setQ] = useState('');
  const [sort] = useState('manual');
  const [draggedStudyId, setDraggedStudyId] = useState(null);
  const [dragPreviewOrder, setDragPreviewOrder] = useState(null);
  const dragPreviewOrderRef = useRef(null);
  const [draggedFolderId, setDraggedFolderId] = useState(null);
  const folderWasDraggedRef = useRef(false);
  const [teachers, setTeachers] = useState([]);

  const [dialog, setDialog] = useState(null); // {mode:'create'|'edit', study?}
  const [form, setForm] = useState(emptyForm);
  const [shareFor, setShareFor] = useState(null);
  const [shareSel, setShareSel] = useState([]);
  const [folderDialog, setFolderDialog] = useState(null); // {mode:'create'|'edit', folder?}
  const [folderName, setFolderName] = useState('');
  const [moveFor, setMoveFor] = useState(null);
  const [moveTo, setMoveTo] = useState(NO_FOLDER);
  const [assignFor, setAssignFor] = useState(null);   // folder to assign teachers to
  const [assignSel, setAssignSel] = useState([]);     // teacher user_ids

  const load = useCallback(async () => {
    try { const { data } = await api.get('/studies'); setStudies({ mine: data.mine || [], shared: data.shared || [], public: data.public || [], assigned: data.assigned || [] }); } catch { /* silent */ }
    try {
      const { data } = await api.get('/study-folders');
      setFolders(data.folders || []);
      setUnfolderedCount(Number(data.unfoldered_count || 0));
    } catch { /* silent */ }
  }, []);
  useEffect(() => { load(); }, [load]);
  // Teachers also need the teacher directory so the owner can pick who to
  // assign a folder to. `/users` is gated to teacher/school_admin/super_admin.
  useEffect(() => {
    api.get('/users').then(r => setTeachers((r.data||[]).filter(u => u.role === 'teacher'))).catch(()=>{});
  }, []);

  // Owned folder counts derived from my studies (kept in sync locally).
  const availableModules = useMemo(() => {
    const modules = Array.from({ length: 10 }, (_, i) => `Módulo ${i + 1}`);
    (studies.mine || []).forEach(s => {
      const module = (s.module || '').trim();
      if (module) modules.push(module);
    });
    if (form.module?.trim()) modules.push(form.module.trim());
    return [...new Set(modules)].sort((a, b) => {
      const na = Number(a.match(/^Módulo\s+(\d+)$/i)?.[1]);
      const nb = Number(b.match(/^Módulo\s+(\d+)$/i)?.[1]);
      if (na && nb) return na - nb;
      if (na) return -1;
      if (nb) return 1;
      return a.localeCompare(b, 'es');
    });
  }, [studies.mine, form.module]);

  const ownedFolders = useMemo(() => folders.filter(f => f.owned), [folders]);
  const assignedFolders = useMemo(() => folders.filter(f => !f.owned), [folders]);

  const foldersWithCounts = useMemo(() => {
    const counts = {};
    let un = 0;
    (studies.mine || []).forEach(s => {
      const fid = s.folder_id || null;
      if (!fid) un += 1;
      else counts[fid] = (counts[fid] || 0) + 1;
    });
    const list = ownedFolders.map(f => ({ ...f, study_count: counts[f.id] ?? f.study_count ?? 0 }));
    return { list, un };
  }, [ownedFolders, studies.mine]);

  const currentFolder = useMemo(() => {
    if (!currentFolderId || currentFolderId === NO_FOLDER) return null;
    return folders.find(f => f.id === currentFolderId) || null;
  }, [currentFolderId, folders]);

  // Studies visible inside the current folder view.
  const list = useMemo(() => {
    let arr;
    if (sub === 'mine') {
      arr = studies.mine;
      if (currentFolderId === NO_FOLDER) arr = arr.filter(s => !s.folder_id);
      else if (currentFolderId) arr = arr.filter(s => s.folder_id === currentFolderId);
    } else {
      arr = [...studies.assigned, ...studies.shared, ...studies.public];
      // Filter to the entered assigned folder only.
      if (currentFolderId && currentFolderId !== NO_FOLDER) arr = arr.filter(s => s.folder_id === currentFolderId);
    }
    if (q) arr = arr.filter(s => (s.name||'').toLowerCase().includes(q.toLowerCase()) || (s.module||'').toLowerCase().includes(q.toLowerCase()));
    arr = [...arr];
    arr.sort((a,b) => {
      const moduleCompare = (a.module || '').localeCompare(b.module || '', 'es');
      if (moduleCompare) return moduleCompare;
      const ao = Number.isFinite(a.study_order) ? a.study_order : Number.MAX_SAFE_INTEGER;
      const bo = Number.isFinite(b.study_order) ? b.study_order : Number.MAX_SAFE_INTEGER;
      if (ao !== bo) return ao - bo;
      return (b.created_at || '').localeCompare(a.created_at || '');
    });
    return arr;
  }, [studies, sub, q, sort, currentFolderId]);

  const displayList = useMemo(() => {
    if (!dragPreviewOrder) return list;

    const positions = new Map(dragPreviewOrder.map((id, index) => [id, index]));
    const moduleOrders = new Map();

    for (const item of list) {
      const moduleName = item.module || '';
      if (!moduleOrders.has(moduleName)) moduleOrders.set(moduleName, []);
      moduleOrders.get(moduleName).push(item);
    }

    const orderedByModule = new Map();
    for (const [moduleName, items] of moduleOrders) {
      orderedByModule.set(
        moduleName,
        [...items].sort((a, b) => {
          const aPosition = positions.get(a.id);
          const bPosition = positions.get(b.id);
          if (aPosition === undefined || bPosition === undefined) {
            return items.indexOf(a) - items.indexOf(b);
          }
          return aPosition - bPosition;
        })
      );
    }

    const offsets = new Map();
    return list.map(item => {
      const moduleName = item.module || '';
      const moduleItems = orderedByModule.get(moduleName);
      const index = offsets.get(moduleName) || 0;
      offsets.set(moduleName, index + 1);
      return moduleItems[index];
    });
  }, [list, dragPreviewOrder]);

  const commitFolderOrder = async (orderedFolderIds) => {
    if (!isOwnerAdmin || sub !== 'mine' || !Array.isArray(orderedFolderIds)) return;

    const currentIds = ownedFolders.map(folder => folder.id);
    if (
      orderedFolderIds.length !== currentIds.length ||
      new Set(orderedFolderIds).size !== currentIds.length ||
      !orderedFolderIds.every(id => currentIds.includes(id))
    ) return;

    if (orderedFolderIds.every((id, index) => id === currentIds[index])) return;

    try {
      await Promise.all(
        orderedFolderIds.map((id, index) =>
          api.patch(`/study-folders/${id}`, { order: index + 1 })
        )
      );
      toast.success('Orden de carpetas guardado');
      await load();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'No se pudo guardar el orden de las carpetas');
      await load();
    }
  };

  const commitPreviewOrder = async (sourceId, previewOrder) => {
    if (!isOwnerAdmin || sub !== 'mine' || q.trim() || !sourceId || !previewOrder) return;

    const source = list.find(item => item.id === sourceId);
    if (!source) return;

    const moduleName = source.module || '';
    const sameModule = list.filter(item => (item.module || '') === moduleName);
    const visibleIds = previewOrder.filter(id =>
      sameModule.some(item => item.id === id)
    );
    if (visibleIds.length !== sameModule.length) return;

    const originalIds = sameModule.map(item => item.id);
    if (visibleIds.every((id, index) => id === originalIds[index])) return;

    try {
      await api.post('/studies/reorder', {
        module: moduleName,
        folder_id: currentFolderId === NO_FOLDER ? null : currentFolderId,
        order: visibleIds,
      });
      toast.success('Orden de actividades guardado');
      await load();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'No se pudo guardar el orden');
      await load();
    }
  };

  const reorderStudies = async (sourceStudy, targetStudy) => {
    if (!isOwnerAdmin || sub !== 'mine' || q.trim() || !sourceStudy || !targetStudy) return;
    if (sourceStudy.id === targetStudy.id) return;

    const moduleName = sourceStudy.module || '';
    if ((targetStudy.module || '') !== moduleName) return;

    const sameModule = list.filter(item => (item.module || '') === moduleName);
    const sourceIndex = sameModule.findIndex(item => item.id === sourceStudy.id);
    const targetIndex = sameModule.findIndex(item => item.id === targetStudy.id);
    if (sourceIndex < 0 || targetIndex < 0) return;

    const order = sameModule.map(item => item.id);
    const [moved] = order.splice(sourceIndex, 1);
    order.splice(targetIndex, 0, moved);

    try {
      await api.post('/studies/reorder', {
        module: moduleName,
        folder_id: currentFolderId === NO_FOLDER ? null : currentFolderId,
        order,
      });
      toast.success('Orden de actividades guardado');
      await load();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'No se pudo guardar el orden');
      await load();
    }
  };

  // A teacher (non-owner) never sees the "My Studies" tab in practice
  // because they don't create their own studies in the folder-assignment
  // model. Still, keep the tab available if they happen to own any (backward
  // compat) — but default them to Shared/Assigned on load.
  const canCreate = isOwnerAdmin;   // only director/admin creates studies + folders

  const openCreate = () => {
    const preselect = (sub === 'mine' && currentFolderId && currentFolderId !== NO_FOLDER)
      ? currentFolderId : NO_FOLDER;
    setForm({ ...emptyForm, folder_id: preselect });
    setDialog({ mode:'create' });
  };
  const openEdit = (s) => {
    setForm({
      name:s.name, description:s.description||'', module:s.module||'',
      level:s.level||'beginner', visibility:s.visibility||'private',
      folder_id: s.folder_id || NO_FOLDER,
    });
    setDialog({ mode:'edit', study:s });
  };

  const submitDialog = async () => {
    if (!form.name.trim()) { toast.error('Nombre requerido'); return; }
    const payload = { ...form, folder_id: form.folder_id === NO_FOLDER ? null : form.folder_id };
    try {
      if (dialog.mode === 'create') { await api.post('/studies', payload); toast.success('Estudio creado'); }
      else { await api.patch(`/studies/${dialog.study.id}`, payload); toast.success('Estudio actualizado'); }
      setDialog(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const removeStudy = async (s) => {
    if (!window.confirm(`¿Borrar el estudio "${s.name}"?`)) return;
    try { await api.delete(`/studies/${s.id}`); toast.success('Estudio borrado'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const openShare = (s) => { setShareFor(s); setShareSel(s.shared_with || []); };
  const saveShare = async () => {
    try {
      await api.patch(`/studies/${shareFor.id}`, { shared_with: shareSel, visibility: shareSel.length ? 'shared' : 'private' });
      toast.success('Asignación guardada'); setShareFor(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  // ---------- Folder actions ----------
  const openNewFolder = () => { setFolderName(''); setFolderDialog({ mode: 'create' }); };
  const openRenameFolder = (f) => { setFolderName(f.name); setFolderDialog({ mode: 'edit', folder: f }); };
  const submitFolder = async () => {
    const n = folderName.trim();
    if (!n) { toast.error('Nombre requerido'); return; }
    try {
      if (folderDialog.mode === 'create') { await api.post('/study-folders', { name: n }); toast.success('Carpeta creada'); }
      else { await api.patch(`/study-folders/${folderDialog.folder.id}`, { name: n }); toast.success('Carpeta renombrada'); }
      setFolderDialog(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };
  const deleteFolder = async (f) => {
    if (!window.confirm(`¿Eliminar la carpeta "${f.name}"?\n\nLos Studies NO se eliminan, pasarán a "Sin carpeta".`)) return;
    try {
      await api.delete(`/study-folders/${f.id}`);
      toast.success('Carpeta eliminada · Studies movidos a "Sin carpeta"');
      if (currentFolderId === f.id) setCurrentFolderId(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const openMove = (s) => { setMoveFor(s); setMoveTo(s.folder_id || NO_FOLDER); };
  const submitMove = async () => {
    try {
      await api.patch(`/studies/${moveFor.id}`, { folder_id: moveTo === NO_FOLDER ? null : moveTo });
      toast.success('Study movido'); setMoveFor(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  // ---------- Folder → teachers assignment ----------
  const openAssign = (f) => {
    setAssignFor(f);
    setAssignSel(Array.isArray(f.assigned_teacher_ids) ? [...f.assigned_teacher_ids] : []);
  };
  const submitAssign = async () => {
    try {
      await api.patch(`/study-folders/${assignFor.id}`, { assigned_teacher_ids: assignSel });
      toast.success('Asignación guardada'); setAssignFor(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const backToFolders = () => setCurrentFolderId(null);

  // ---------- View decisions ----------
  // Owners see the "Mine" folder-list view. Non-owners (assigned teachers)
  // see their assigned folder list. Both use the same root layout.
  const rootView = tab === 'study' && !currentFolderId;
  const showingOwnerFolders = rootView && sub === 'mine';
  const showingAssignedFolders = rootView && sub !== 'mine';

  return (
    <div className="min-h-[calc(100vh-4rem)]">
      {isOwnerAdmin && (
        <div className="border-b border-slate-200 px-6 pt-4 flex gap-6">
          {[['study','Estudios'],['collection','Colecciones']].map(([k,l]) => (
            <button key={k} data-testid={`studies-tab-${k}`} onClick={()=>setTab(k)}
              className={`pb-3 text-sm font-semibold border-b-2 -mb-px ${tab===k?'border-indigo-600 text-indigo-600':'border-transparent text-slate-500 hover:text-slate-700'}`}>{l}</button>
          ))}
        </div>
      )}

      {tab === 'collection' && isOwnerAdmin ? (
        <div className="grid place-items-center h-[60vh] text-center text-slate-500">
          <div>
            <Layers className="w-10 h-10 mx-auto mb-3 text-slate-300"/>
            <div className="font-semibold">Colecciones</div>
            <div className="text-sm text-slate-400 mt-1">Agrupa varios estudios en un curso. Próximamente.</div>
          </div>
        </div>
      ) : (
        <div className="p-6">
          <div className="flex justify-end mb-3 gap-2">
            {canCreate && sub === 'mine' && (
              <Button data-testid="new-folder-btn" onClick={openNewFolder} variant="outline" className="rounded-lg px-4">
                <FolderPlus className="w-4 h-4 mr-1"/> Nueva carpeta
              </Button>
            )}
            {canCreate && (
              <Button data-testid="new-study-btn" onClick={openCreate} className="rounded-lg bg-indigo-600 hover:bg-indigo-700 px-6"><Plus className="w-4 h-4 mr-1"/> Create</Button>
            )}
          </div>
          <div className="flex gap-3 mb-4 items-center">
            <Input data-testid="studies-search" placeholder="Buscar estudio…" value={q} onChange={e=>setQ(e.target.value)} className="rounded-lg"/>
            <div data-testid="studies-sort" className="w-56 shrink-0 rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-700">
              Actividades
            </div>
          </div>
          {isOwnerAdmin ? (
            <div className="flex gap-6 border-b border-slate-200 mb-4">
              {[['mine','Mis estudios'],['shared','Estudios compartidos']].map(([k,l]) => (
                <button key={k} data-testid={`studies-sub-${k}`}
                  onClick={()=>{ setSub(k); setCurrentFolderId(null); }}
                  className={`pb-2 text-sm font-semibold border-b-2 -mb-px ${sub===k?'border-indigo-600 text-indigo-600':'border-transparent text-slate-500 hover:text-slate-700'}`}>{l}</button>
              ))}
            </div>
          ) : (
            <div className="mb-4">
              <h2 className="font-display text-xl font-bold text-slate-900">Estudios asignados</h2>
              <p className="text-sm text-slate-500 mt-1">Aquí encontrarás los estudios que el Owner te ha asignado.</p>
            </div>
          )}

          {/* Breadcrumb inside a folder */}
          {currentFolderId && (
            <div className="mb-3 flex items-center gap-2 text-sm">
              <button data-testid="folders-back-btn" onClick={backToFolders}
                className="inline-flex items-center gap-1 text-indigo-600 hover:underline font-semibold">
                <ArrowLeft className="w-4 h-4"/> Estudios
              </button>
              <span className="text-slate-400">/</span>
              <span className="inline-flex items-center gap-1 font-semibold text-slate-800">
                {currentFolderId === NO_FOLDER
                  ? (<><FolderOpen className="w-4 h-4 text-slate-500"/> Sin carpeta</>)
                  : (<><FolderOpen className="w-4 h-4 text-indigo-500"/> {currentFolder?.name || 'Carpeta'}</>)}
              </span>
              {currentFolder && !currentFolder.owned && (
                <span className="ml-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-slate-500 bg-slate-100 rounded-full px-2 py-0.5">
                  <Lock className="w-3 h-3"/> Sólo lectura
                </span>
              )}
              <span className="text-xs text-slate-500 ml-2">
                {list.length} {list.length === 1 ? 'estudio' : 'estudios'}
              </span>
            </div>
          )}

          {/* OWNER FOLDER LIST (root, "My Studies") */}
          {showingOwnerFolders && (
            <div className="space-y-3" data-testid="folders-root">
              {foldersWithCounts.list.length === 0 && foldersWithCounts.un === 0 && studies.mine.length === 0 && (
                <div className="rounded-2xl border border-dashed border-slate-300 p-12 text-center text-slate-500">
                  <GraduationCap className="w-8 h-8 mx-auto mb-2 text-slate-300"/> No hay estudios aquí.
                </div>
              )}
              {foldersWithCounts.list.map(f => (
                <div key={f.id} data-testid={`folder-card-${f.id}`}
                  draggable={isOwnerAdmin && sub === 'mine'}
                  onDragStart={e => {
                    if (!isOwnerAdmin || sub !== 'mine') {
                      e.preventDefault();
                      return;
                    }
                    folderWasDraggedRef.current = false;
                    setDraggedFolderId(f.id);
                    e.dataTransfer.effectAllowed = 'move';
                    e.dataTransfer.setData('text/plain', f.id);
                  }}
                  onDragOver={e => {
                    if (isOwnerAdmin && sub === 'mine') {
                      e.preventDefault();
                      e.dataTransfer.dropEffect = 'move';
                    }
                  }}
                  onDrop={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    const sourceId = draggedFolderId || e.dataTransfer.getData('text/plain');
                    setDraggedFolderId(null);
                    if (!sourceId || sourceId === f.id) return;
                    folderWasDraggedRef.current = true;
                    const orderedIds = foldersWithCounts.list.map(folder => folder.id);
                    const sourceIndex = orderedIds.indexOf(sourceId);
                    const targetIndex = orderedIds.indexOf(f.id);
                    if (sourceIndex < 0 || targetIndex < 0) return;
                    orderedIds.splice(sourceIndex, 1);
                    orderedIds.splice(targetIndex, 0, sourceId);
                    void commitFolderOrder(orderedIds);
                  }}
                  onDragEnd={() => setDraggedFolderId(null)}
                  className={`rounded-2xl bg-white hover:bg-slate-50 border border-slate-200 p-4 flex items-center gap-4 cursor-pointer transition-colors ${draggedFolderId === f.id ? 'opacity-50' : ''}`}
                  onClick={() => {
                    if (folderWasDraggedRef.current) {
                      folderWasDraggedRef.current = false;
                      return;
                    }
                    setCurrentFolderId(f.id);
                  }}>
                  <div className="w-14 h-14 rounded-xl bg-indigo-50 border border-indigo-100 grid place-items-center shrink-0">
                    <Folder className="w-7 h-7 text-indigo-500"/>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-lg font-bold truncate">{f.name}</div>
                    <div className="text-xs text-slate-500 flex items-center gap-2">
                      <span>{f.study_count} {f.study_count === 1 ? 'estudio' : 'estudios'}</span>
                      {Array.isArray(f.assigned_teacher_ids) && f.assigned_teacher_ids.length > 0 && (
                        <span className="inline-flex items-center gap-1 text-indigo-600 font-semibold">
                          · <Users className="w-3 h-3"/> {f.assigned_teacher_ids.length} profe{f.assigned_teacher_ids.length===1?'':'s'}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="shrink-0 flex items-center gap-2" onClick={(e)=>e.stopPropagation()}>
                    <Button data-testid={`folder-assign-${f.id}`} size="sm" variant="outline"
                      className="rounded-full text-xs" onClick={()=>openAssign(f)}>
                      <Users className="w-3.5 h-3.5 mr-1"/> Asignar profesores
                    </Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button data-testid={`folder-menu-${f.id}`} className="p-1.5 hover:bg-slate-200 rounded"><MoreVertical className="w-4 h-4"/></button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem data-testid={`folder-rename-${f.id}`} onClick={()=>openRenameFolder(f)}><Pencil className="w-3.5 h-3.5 mr-2"/> Renombrar</DropdownMenuItem>
                        <DropdownMenuItem data-testid={`folder-delete-${f.id}`} onClick={()=>deleteFolder(f)} className="text-rose-600"><Trash2 className="w-3.5 h-3.5 mr-2"/> Eliminar</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              ))}
              {/* "Sin carpeta" always visible for the owner */}
              <div data-testid="folder-card-unfoldered"
                className="rounded-2xl bg-white hover:bg-slate-50 border border-dashed border-slate-300 p-4 flex items-center gap-4 cursor-pointer"
                onClick={()=>setCurrentFolderId(NO_FOLDER)}>
                <div className="w-14 h-14 rounded-xl bg-slate-100 grid place-items-center shrink-0">
                  <FolderOpen className="w-7 h-7 text-slate-500"/>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display text-lg font-bold truncate">Sin carpeta</div>
                  <div className="text-xs text-slate-500">
                    {foldersWithCounts.un} {foldersWithCounts.un === 1 ? 'estudio' : 'estudios'}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ASSIGNED FOLDER LIST (root, "Assigned to me" / "Shared Studies") */}
          {showingAssignedFolders && (
            <div className="space-y-3" data-testid="assigned-folders-root">
              {assignedFolders.length === 0 && (studies.shared || []).length === 0 && (studies.public || []).length === 0 && (
                <div className="rounded-2xl border border-dashed border-slate-300 p-12 text-center text-slate-500">
                  <Lock className="w-8 h-8 mx-auto mb-2 text-slate-300"/> Aún no tienes estudios asignados.
                </div>
              )}
              {assignedFolders.map(f => (
                <div key={f.id} data-testid={`assigned-folder-card-${f.id}`}
                  className="rounded-2xl bg-white hover:bg-slate-50 border border-slate-200 p-4 flex items-center gap-4 cursor-pointer transition-colors"
                  onClick={()=>setCurrentFolderId(f.id)}>
                  <div className="w-14 h-14 rounded-xl bg-amber-50 border border-amber-100 grid place-items-center shrink-0">
                    <Folder className="w-7 h-7 text-amber-500"/>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-lg font-bold truncate">{f.name}</div>
                    <div className="text-xs text-slate-500 flex items-center gap-2">
                      <span>{f.study_count} {f.study_count === 1 ? 'estudio' : 'estudios'}</span>
                      <span className="inline-flex items-center gap-1 text-slate-500">
                        · <Lock className="w-3 h-3"/> Sólo lectura
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* STUDY LIST inside a folder */}
          {!rootView && (
            <div
              className="space-y-3"
              data-testid="study-list-dropzone"
              onDragOver={e => {
                if (draggedStudyId && sub === 'mine' && isOwnerAdmin && !q.trim()) {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'move';
                }
              }}
              onDrop={e => {
                e.preventDefault();
                e.stopPropagation();
                const sourceId = draggedStudyId || e.dataTransfer.getData('text/plain');
                const source = list.find(item => item.id === sourceId);
                const previewOrder = dragPreviewOrderRef.current || dragPreviewOrder;
                dragPreviewOrderRef.current = null;
                setDraggedStudyId(null);
                setDragPreviewOrder(null);
                if (source && previewOrder) void commitPreviewOrder(sourceId, previewOrder);
              }}
            >
              {list.length === 0 && (
                <div className="rounded-2xl border border-dashed border-slate-300 p-12 text-center text-slate-500">
                  <GraduationCap className="w-8 h-8 mx-auto mb-2 text-slate-300"/> No hay estudios aquí.
                </div>
              )}
              {displayList.map(s => {
                // Can I edit this study? Only if I'm the actual owner or an admin.
                const canEdit = isOwnerAdmin || s.owner_id === user?.id;
                const lvl = LEVELS[s.level] || LEVELS.beginner;
                return (
                  <motion.div layout transition={{ layout: { type: 'spring', stiffness: 180, damping: 26, mass: 1 } }}
                    key={s.id} data-testid={`study-card-${s.id}`}
                    draggable={sub === 'mine' && isOwnerAdmin && !q.trim()}
                    onDragStart={e => {
                      if (sub !== 'mine' || !isOwnerAdmin || q.trim()) { e.preventDefault(); return; }
                      setDraggedStudyId(s.id);
                      const initialOrder = list.map(item => item.id);
                      dragPreviewOrderRef.current = initialOrder;
                      setDragPreviewOrder(initialOrder);
                      e.dataTransfer.effectAllowed = 'move';
                      e.dataTransfer.setData('text/plain', s.id);
                    }}
                    onDragOver={e => {
                      const source = list.find(item => item.id === draggedStudyId);
                      if (source && (source.module || '') === (s.module || '') && source.id !== s.id) {
                        e.preventDefault();
                        e.dataTransfer.dropEffect = 'move';

                        const ids = displayList.map(item => item.id);
                        const from = ids.indexOf(source.id);
                        if (from < 0) return;

                        ids.splice(from, 1);
                        const targetIndex = ids.indexOf(s.id);
                        if (targetIndex < 0) return;

                        const rect = e.currentTarget.getBoundingClientRect();
                        const midpoint = rect.top + rect.height / 2;
                        const deadZone = Math.min(12, rect.height * 0.08);
                        const previousOrder = dragPreviewOrderRef.current;
                        const sourcePosition = previousOrder
                          ? previousOrder.indexOf(source.id)
                          : from;
                        const targetPosition = previousOrder
                          ? previousOrder.indexOf(s.id)
                          : targetIndex;

                        let insertAfter = e.clientY > midpoint + deadZone;
                        if (e.clientY >= midpoint - deadZone &&
                            e.clientY <= midpoint + deadZone) {
                          insertAfter = sourcePosition > targetPosition;
                        }

                        const insertIndex = targetIndex + (insertAfter ? 1 : 0);
                        ids.splice(insertIndex, 0, source.id);

                        setDragPreviewOrder(previous => {
                          const nextOrder = previous && previous.length === ids.length &&
                            previous.every((id, index) => id === ids[index])
                              ? previous : ids;
                          dragPreviewOrderRef.current = nextOrder;
                          return nextOrder;
                        });
                      }
                    }}
                    onDrop={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      const sourceId = draggedStudyId || e.dataTransfer.getData('text/plain');
                      const source = list.find(item => item.id === sourceId);
                      const previewOrder = dragPreviewOrderRef.current || dragPreviewOrder;
                      if (source && previewOrder) void commitPreviewOrder(sourceId, previewOrder);
                      else if (source) void reorderStudies(source, s);
                      dragPreviewOrderRef.current = null;
                      setDraggedStudyId(null);
                      setDragPreviewOrder(null);
                    }}
                    onDragEnd={() => {
                      dragPreviewOrderRef.current = null;
                      setDraggedStudyId(null);
                      setDragPreviewOrder(null);
                    }}
                    whileDrag={{ scale: 1.025, boxShadow: '0 14px 28px rgba(15, 23, 42, 0.18)', zIndex: 10 }}
                    className={`relative rounded-2xl bg-slate-50 hover:bg-slate-100 border border-slate-200 p-4 flex items-center gap-4 transition-colors ${sub === 'mine' && isOwnerAdmin && !q.trim() ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'} ${draggedStudyId === s.id ? 'opacity-90 border-indigo-300' : ''}`}
                    onClick={() => {
                      if (draggedStudyId) return;
                      nav(`${base}/${s.id}`);
                    }}>
                    <div className="w-20 h-20 bg-white rounded-xl border border-slate-200 grid place-items-center shrink-0">
                      <div className="text-3xl font-extrabold leading-none">{s.activities_count ?? 0}</div>
                      <div className="text-[10px] text-slate-500 uppercase tracking-widest">Activities</div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-display text-xl font-bold truncate">{s.name}</div>
                      <div className="text-xs text-slate-500">Created by {s.owner_name || '-'} at {fmtDate(s.created_at)}</div>
                      <div className="text-xs text-slate-500">Total time required: {fmtTime(s.total_time)}, Level: <span className={`font-semibold ${lvl.cls}`}>{lvl.label}</span>{s.module ? ` · ${s.module}` : ''}</div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0" onClick={(e)=>e.stopPropagation()}>
                      {sub === 'mine' && canEdit && !q.trim() && (
                        <div title="Arrastra para cambiar el orden" aria-label={`Arrastrar ${s.name}`} className="text-slate-400 cursor-grab">
                          <GripVertical className="w-5 h-5"/>
                        </div>
                      )}
                      <button data-testid={`open-study-${s.id}`} onClick={()=>nav(`${base}/${s.id}`)}
                        className="text-indigo-600 font-semibold text-sm inline-flex items-center gap-1 hover:underline">
                        <PlusCircle className="w-4 h-4"/> {canEdit ? 'Create Quiz' : 'Ver / Usar'}
                      </button>
                      {canEdit ? (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button data-testid={`study-menu-${s.id}`} className="p-1.5 hover:bg-slate-200 rounded"><MoreVertical className="w-4 h-4"/></button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem data-testid={`study-edit-${s.id}`} onClick={()=>openEdit(s)}><Pencil className="w-3.5 h-3.5 mr-2"/> Edit</DropdownMenuItem>
                            <DropdownMenuItem data-testid={`study-move-${s.id}`} onClick={()=>openMove(s)}><FolderInput className="w-3.5 h-3.5 mr-2"/> Mover a carpeta</DropdownMenuItem>
                            <DropdownMenuItem data-testid={`study-delete-${s.id}`} onClick={()=>removeStudy(s)} className="text-rose-600"><Trash2 className="w-3.5 h-3.5 mr-2"/> Delete</DropdownMenuItem>
                            {isOwnerAdmin && <DropdownMenuItem data-testid={`study-share-${s.id}`} onClick={()=>openShare(s)}><Share2 className="w-3.5 h-3.5 mr-2"/> Share</DropdownMenuItem>}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      ) : (
                        <span data-testid={`study-readonly-${s.id}`} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-slate-500 bg-white rounded-full px-2 py-1 border border-slate-200">
                          <Lock className="w-3 h-3"/> Sólo lectura
                        </span>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Create / Edit dialog */}
      <Dialog open={!!dialog} onOpenChange={(v)=>{ if(!v) setDialog(null); }}>
        <DialogContent data-testid="study-dialog">
          <DialogHeader><DialogTitle>{dialog?.mode==='edit'?'Editar estudio':'Nuevo estudio'}</DialogTitle>
            <DialogDescription>Un estudio agrupa actividades/quizzes para tus alumnos.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Nombre</Label><Input data-testid="study-name" value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} placeholder="Ej: Módulo 6 Mate Rey y Torre"/></div>
            <div><Label>Descripción</Label><Input data-testid="study-desc" value={form.description} onChange={e=>setForm(f=>({...f,description:e.target.value}))}/></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Módulo</Label>
                <Select value={form.module || '__none__'} onValueChange={v=>setForm(f=>({...f,module:v==='__none__'?'':v}))}>
                  <SelectTrigger data-testid="study-module"><SelectValue placeholder="Seleccionar módulo"/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Sin módulo</SelectItem>
                    {availableModules.map(module => (
                      <SelectItem key={module} value={module}>{module}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Nivel</Label>
                <Select value={form.level} onValueChange={v=>setForm(f=>({...f,level:v}))}>
                  <SelectTrigger data-testid="study-level"><SelectValue/></SelectTrigger>
                  <SelectContent><SelectItem value="beginner">Beginner</SelectItem><SelectItem value="intermediate">Intermediate</SelectItem><SelectItem value="advanced">Advanced</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Carpeta</Label>
              <Select value={form.folder_id || NO_FOLDER} onValueChange={v=>setForm(f=>({...f,folder_id:v}))}>
                <SelectTrigger data-testid="study-folder"><SelectValue placeholder="Sin carpeta"/></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_FOLDER}>Sin carpeta</SelectItem>
                  {ownedFolders.map(f => (
                    <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Visibilidad</Label>
              <Select value={form.visibility} onValueChange={v=>setForm(f=>({...f,visibility:v}))}>
                <SelectTrigger data-testid="study-vis"><SelectValue/></SelectTrigger>
                <SelectContent><SelectItem value="private">Privado</SelectItem><SelectItem value="shared">Compartido</SelectItem><SelectItem value="public">Público</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button data-testid="study-submit" onClick={submitDialog} className="bg-indigo-600 hover:bg-indigo-700">{dialog?.mode==='edit'?'Guardar':'Crear'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Folder create / rename dialog */}
      <Dialog open={!!folderDialog} onOpenChange={(v)=>{ if(!v) setFolderDialog(null); }}>
        <DialogContent data-testid="folder-dialog">
          <DialogHeader>
            <DialogTitle>{folderDialog?.mode === 'edit' ? 'Renombrar carpeta' : 'Nueva carpeta'}</DialogTitle>
            <DialogDescription>Las carpetas organizan tus Studies por módulo o tema.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Label>Nombre</Label>
            <Input data-testid="folder-name-input" value={folderName} onChange={e=>setFolderName(e.target.value)} placeholder="Módulo 1"/>
          </div>
          <DialogFooter>
            <Button data-testid="folder-submit" onClick={submitFolder} className="bg-indigo-600 hover:bg-indigo-700">
              {folderDialog?.mode === 'edit' ? 'Guardar' : 'Crear'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Move study to folder dialog */}
      <Dialog open={!!moveFor} onOpenChange={(v)=>{ if(!v) setMoveFor(null); }}>
        <DialogContent data-testid="move-study-dialog">
          <DialogHeader>
            <DialogTitle>Mover Study</DialogTitle>
            <DialogDescription>Elige la carpeta destino para "{moveFor?.name}".</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Label>Carpeta</Label>
            <Select value={moveTo} onValueChange={setMoveTo}>
              <SelectTrigger data-testid="move-folder-select"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_FOLDER}>Sin carpeta</SelectItem>
                {ownedFolders.map(f => (
                  <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button data-testid="move-submit" onClick={submitMove} className="bg-indigo-600 hover:bg-indigo-700">Mover</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign folder → teachers dialog */}
      <Dialog open={!!assignFor} onOpenChange={(v)=>{ if(!v) setAssignFor(null); }}>
        <DialogContent data-testid="assign-folder-dialog">
          <DialogHeader>
            <DialogTitle>Asignar profesores</DialogTitle>
            <DialogDescription>
              Los profesores seleccionados podrán ver y utilizar los Studies de <b>{assignFor?.name}</b> en sólo lectura. Nuevos Studies dentro de la carpeta se comparten automáticamente.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1 max-h-80 overflow-y-auto">
            {teachers.length === 0 && <div className="text-sm text-slate-500">No hay profesores.</div>}
            {teachers.map(t => {
              const checked = assignSel.includes(t.id);
              return (
                <label key={t.id} data-testid={`assign-teacher-${t.id}`} className="flex items-center gap-2 p-2 rounded hover:bg-slate-50 cursor-pointer">
                  <input type="checkbox" checked={checked} onChange={()=>setAssignSel(s => checked ? s.filter(x=>x!==t.id) : [...s, t.id])}/>
                  <span className="text-sm">{t.first_name} {t.last_name} <span className="text-slate-400 text-xs">({t.email})</span></span>
                </label>
              );
            })}
          </div>
          <DialogFooter>
            <Button data-testid="assign-submit" onClick={submitAssign} className="bg-indigo-600 hover:bg-indigo-700">Guardar asignación</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Share dialog (legacy per-study share) */}
      <Dialog open={!!shareFor} onOpenChange={(v)=>{ if(!v) setShareFor(null); }}>
        <DialogContent data-testid="share-study-dialog">
          <DialogHeader><DialogTitle>Asignar a profesores</DialogTitle>
            <DialogDescription>Los profesores seleccionados podrán usar este estudio.</DialogDescription>
          </DialogHeader>
          <div className="space-y-1 max-h-80 overflow-y-auto">
            {teachers.length === 0 && <div className="text-sm text-slate-500">No hay profesores.</div>}
            {teachers.map(t => {
              const checked = shareSel.includes(t.id);
              return (
                <label key={t.id} data-testid={`share-teacher-${t.id}`} className="flex items-center gap-2 p-2 rounded hover:bg-slate-50 cursor-pointer">
                  <input type="checkbox" checked={checked} onChange={()=>setShareSel(s => checked ? s.filter(x=>x!==t.id) : [...s, t.id])}/>
                  <span className="text-sm">{t.first_name} {t.last_name} <span className="text-slate-400 text-xs">({t.email})</span></span>
                </label>
              );
            })}
          </div>
          <DialogFooter><Button data-testid="share-submit" onClick={saveShare} className="bg-indigo-600 hover:bg-indigo-700">Guardar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
